import { useEffect, useState } from "react";
import { Plus, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, Panel } from "@/components/crm/common";
import { FormDialog } from "@/components/crm/FormDialog";
import { UsersPanel } from "@/components/crm/UsersPanel";
import { FinanceLockSettings } from "@/components/crm/FinanceLockSettings";
import { useAuth } from "@/context/AuthContext";

const MODS = ["Dashboard", "Projects", "Pitch", "Assignments", "Tracker", "Handover", "Execution", "Approvals", "Team", "Budget", "HR", "Roles", "Overview", "Vendors", "VendorExpenses", "InternalExpenses", "POC", "ExpenseReports", "Meetings", "Sales", "Procurement", "Invoices", "Production"];
const ACTS = ["view", "create", "edit", "approve"];

export default function Roles() {
  const { isAdmin } = useAuth();
  const { items, create, update } = useCollection("roles");
  const { items: activity } = useCollection("activity");
  const [sel, setSel] = useState("");
  const [dlg, setDlg] = useState(false);
  useEffect(() => { if (items.length && !items.find((r) => r.id === sel)) setSel(items[0].id); }, [items, sel]);
  const role = items.find((r) => r.id === sel);

  const toggle = (m, a) => {
    const perms = { ...(role.permissions || {}) };
    perms[m] = { ...(perms[m] || {}), [a]: !perms[m]?.[a] };
    update(role.id, { permissions: perms }, "Permission changed");
  };

  return (
    <>
      <PageHeader num="12" title="Access Control" subtitle="Manage what each role can view, create, edit and approve.">
        {isAdmin && <Button data-testid="create-role-button" onClick={() => setDlg(true)}><Plus className="h-4 w-4 mr-1" />Create Role</Button>}
      </PageHeader>
      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <div className="space-y-3">
          {items.map((r) => (
            <button key={r.id} data-testid={`role-card-${r.id}`} onClick={() => setSel(r.id)}
              className={`w-full text-left rounded-xl border p-4 transition-colors ${r.id === sel ? "border-zinc-900 bg-white shadow-sm" : "border-slate-200 bg-white hover:border-slate-400"}`}>
              <div className="flex items-center justify-between"><span className="font-display font-bold text-slate-900">{r.name}</span>{r.disabled && <span className="text-[10px] text-rose-600 font-semibold">DISABLED</span>}</div>
              <div className="text-xs text-slate-500 mt-1">{r.description}</div>
            </button>
          ))}
        </div>
        {role && (
          <Panel title={`Permission matrix — ${role.name}`} action={isAdmin &&
            <Button data-testid="toggle-role-access-button" size="sm" variant="outline" className={role.disabled ? "" : "text-rose-600"} onClick={() => update(role.id, { disabled: !role.disabled }, role.disabled ? "Access enabled" : "Access disabled")}>
              {role.disabled ? "Enable Access" : "Disable Access"}
            </Button>}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="permission-matrix">
                <thead><tr className="border-b border-slate-200">
                  <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-slate-400">Module</th>
                  {ACTS.map((a) => <th key={a} className="px-3 py-2 text-center text-[11px] uppercase tracking-wider text-slate-400">{a}</th>)}
                </tr></thead>
                <tbody>
                  {MODS.map((m) => (
                    <tr key={m} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2.5 text-slate-700">{m}</td>
                      {ACTS.map((a) => (
                        <td key={a} className={`px-3 py-2.5 text-center ${a === "approve" ? "bg-amber-50/50" : ""}`}>
                          <Checkbox data-testid={`perm-${m.toLowerCase()}-${a}`} disabled={!isAdmin} checked={!!role.permissions?.[m]?.[a]} onCheckedChange={() => toggle(m, a)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-xs text-amber-800">
              <ShieldAlert className="h-4 w-4 shrink-0" />Approval authority (highlighted column) is kept separate from ordinary task-editing permissions. Only authorized administrators can change access settings.
            </div>
          </Panel>
        )}
      </div>
      {isAdmin && <FinanceLockSettings />}
      {isAdmin && <UsersPanel roles={items} />}
      <Panel title="Audit log" testid="audit-log-panel">
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {activity.slice(0, 30).map((a) => (
            <div key={a.id} className="flex justify-between gap-4 text-xs border-b border-slate-100 pb-2">
              <span className="text-slate-700">{a.text}</span><span className="text-slate-400 whitespace-nowrap">{a.by} · {a.created_at?.slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
          {!activity.length && <div className="text-xs text-slate-400">No changes recorded yet.</div>}
        </div>
      </Panel>
      <FormDialog testid="role-form" open={dlg} onOpenChange={setDlg} title="Create role" initial={{}}
        fields={[{ name: "name", label: "Role name", required: true, full: true }, { name: "description", label: "Description", type: "textarea" }]}
        onSubmit={async (f) => !!(await create({ ...f, permissions: {} }, "Role created"))} />
    </>
  );
}
